import {
  memo,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import type { ProductionHistoryWindow } from "@/features/devices/types";
import type { ChartKind, WidgetThreshold } from "../../types";
import { formatWidgetNumber } from "./format";
import {
  FALLBACK_SIZE,
  barIndexAtX,
  buildChartGeometry,
  describeChartInstant,
  describeChartInterval,
  nearestTimeIndex,
  timeAtX,
  type ChartGeometry,
  type ChartSize,
  type GeometryInput,
  type TimeSeriesExtraSeries,
  type TimeSeriesPoint,
  type TimeSeriesReference,
} from "./timeSeriesGeometry";

export type { TimeSeriesExtraSeries, TimeSeriesPoint, TimeSeriesReference } from "./timeSeriesGeometry";
export type { TimeDomain } from "./timeSeriesGeometry";

const UNAVAILABLE_TITLE: Record<"failed" | "missing", string> = {
  failed: "Equipamento lido sem resposta válida · sem valor no período",
  missing: "Sem telemetria no período",
};

function blankReason(point: TimeSeriesPoint): string {
  return point.unavailable ? UNAVAILABLE_TITLE[point.unavailable] : point.unknown
    ? "Período sem telemetria · total desconhecido" : UNAVAILABLE_TITLE.missing;
}

function gapNote(point: TimeSeriesPoint): string {
  return point.includesGapDelta
    ? "Incremento atravessa período sem telemetria · atribuição temporal desconhecida"
    : "Leitura após intervalo sem telemetria";
}

/** Cores das séries, na ordem. A âncora usa a cor padrão da linha. */
const SERIES_COLORS = [
  "var(--brand-600)",
  "var(--warn-500)",
  "var(--ok-500)",
  "var(--danger-500)",
  "var(--ink-500)",
];

const TONE_VAR: Record<Exclude<WidgetThreshold["tone"], "neutral">, string> = {
  ok: "var(--ok-500)",
  warn: "var(--warn-500)",
  danger: "var(--danger-500)",
  info: "var(--brand-500)",
};

interface TimeSeriesChartProps {
  period?: GeometryInput["period"];
  points: TimeSeriesPoint[];
  extraSeries?: TimeSeriesExtraSeries[];
  kind: ChartKind;
  window: ProductionHistoryWindow;
  unit?: string;
  decimals?: number;
  /** `auto` ajusta aos dados; `fixed` usa `min`/`max`. */
  yScale?: "auto" | "fixed";
  min?: number;
  max?: number;
  thresholds?: WidgetThreshold[];
  title: string;
  /** Linha horizontal de referencia (ex.: media do periodo). */
  reference?: TimeSeriesReference | null;
  /**
   * O que o numero do ponto E ("média", "produção"). Vai na frente do valor
   * no tooltip: um bucket de uma hora mostra a media da hora, e o operador
   * precisa ler isso, nao deduzir.
   */
  valueLabel?: string;
  /** Nome da serie ancora no tooltip multisserie. Default: `title`. */
  seriesLabel?: string;
}

/**
 * Cursor sobre o grafico. `time` percorre os instantes de TODAS as series
 * (linha); `bar` percorre as barras; `gap` e o mouse parado num trecho sem
 * bucket nenhum — encaixar no vizinho mais proximo mostraria um valor a
 * quarenta minutos de onde o operador esta olhando. `sticky` = veio do
 * toque/teclado e fica ate o operador sair, em vez de sumir com o dedo.
 */
type Cursor =
  | { mode: "time"; index: number; sticky: boolean }
  | { mode: "gap"; time: number; sticky: boolean }
  | { mode: "bar"; index: number; sticky: boolean };

/** Alem disto do bucket mais proximo, o cursor esta numa lacuna. */
const SNAP_TOLERANCE_BUCKETS = 1.5;

/**
 * Série temporal compartilhada por `line-chart` e `bar-chart`. Recebe os
 * pontos já projetados pela série escolhida (ritmo/produção/acumulado) e
 * desenha eixos X (tempo) e Y (valor + unidade), com linhas de referência
 * para os limites configurados.
 *
 * Desenha em PIXELS REAIS: mede o proprio elemento e usa o tamanho como
 * viewBox. O viewBox fixo esticado (`preserveAspectRatio="none"`) deformava
 * texto e traco — a unidade caia em cima do tick superior e a linha saia
 * "gorda" em cards baixos. Sem medida (primeiro frame, jsdom) cai no tamanho
 * antigo, que os testes de coordenadas conhecem.
 *
 * Navegacao: mouse, dedo e teclado encaixam o cursor no bucket mais proximo
 * por TEMPO e um tooltip diz o instante, o valor de cada serie e — quando nao
 * ha valor — por que nao ha. A geometria e memoizada: mover o mouse nao
 * recalcula os pontos.
 */
export const TimeSeriesChart = memo(function TimeSeriesChart({
  period,
  points,
  extraSeries,
  kind,
  window,
  unit,
  decimals = 1,
  yScale = "auto",
  min,
  max,
  thresholds,
  title,
  reference,
  valueLabel,
  seriesLabel,
}: TimeSeriesChartProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const measured = useMeasuredSize(hostRef);
  const size = measured ?? FALLBACK_SIZE;
  const extras = extraSeries ?? [];
  const isMultiSeries = extras.length > 0;

  const geometry = useMemo(
    () =>
      buildChartGeometry({
        points,
        extraSeries: extras,
        anchorLabel: seriesLabel ?? title,
        kind,
        window,
        unit,
        decimals,
        yScale,
        min,
        max,
        thresholds: thresholds ?? [],
        reference,
        size,
        period,
      }),
    // `extras` e derivado de `extraSeries`; o memo do componente ja garante
    // que uma lista equivalente nao chega aqui como referencia nova.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [points, extraSeries, kind, window, unit, decimals, yScale, min, max, thresholds, reference, title, seriesLabel, size, period],
  );

  const [cursor, setCursor] = useState<Cursor | null>(null);
  // Posicao do ponteiro em coordenadas de viewport, para ancorar o tooltip.
  // Ref, nao estado: a posicao muda a cada pixel e nao precisa rerenderizar.
  const pointerRef = useRef<{ x: number; y: number } | null>(null);
  const tipRef = useRef<HTMLDivElement>(null);

  // Pontos/barras mudaram: um cursor antigo apontaria para um indice que nao
  // existe mais (ou para outro instante). Some, e o proximo movimento recria.
  useEffect(() => setCursor(null), [geometry]);

  const cursorAtClient = (clientX: number, clientY: number, sticky: boolean) => {
    const host = hostRef.current;
    if (!host) return;
    const rect = host.getBoundingClientRect();
    if (rect.width <= 0) return;
    // Sem medida o viewBox e o fallback esticado: escala o X pela largura real.
    const scale = measured ? 1 : FALLBACK_SIZE.w / rect.width;
    const x = (clientX - rect.left) * scale;
    pointerRef.current = { x: clientX, y: clientY };
    if (kind === "bar") {
      const index = barIndexAtX(geometry, x);
      const inside = x >= geometry.padL && x < geometry.padL + geometry.plotW;
      setCursor(index >= 0 ? { mode: "bar", index, sticky } : inside
        ? { mode: "gap", time: timeAtX(geometry, x), sticky } : null);
      return;
    }
    const time = timeAtX(geometry, x);
    const index = nearestTimeIndex(geometry.times, time);
    if (index < 0) {
      setCursor(null);
      return;
    }
    const far = Math.abs(geometry.times[index] - time) > SNAP_TOLERANCE_BUCKETS * geometry.bucketMs;
    setCursor(far ? { mode: "gap", time, sticky } : { mode: "time", index, sticky });
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    // Dedo arrastando segue "grudado" (nao ha leave no toque); o mouse solta
    // ao sair, mesmo que o cursor tenha nascido do teclado.
    cursorAtClient(event.clientX, event.clientY, event.pointerType === "touch");
  };
  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    cursorAtClient(event.clientX, event.clientY, event.pointerType !== "mouse");
  };
  const onPointerLeave = () => {
    if (!cursor?.sticky) setCursor(null);
  };

  // Cursor grudado (toque/teclado) solta ao tocar fora do grafico.
  useEffect(() => {
    if (!cursor?.sticky) return;
    const release = (event: PointerEvent) => {
      if (hostRef.current?.contains(event.target as Node)) return;
      setCursor(null);
    };
    document.addEventListener("pointerdown", release);
    return () => document.removeEventListener("pointerdown", release);
  }, [cursor?.sticky]);

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const length = kind === "bar" ? geometry.bars.length : geometry.times.length;
    if (length === 0) return;
    const mode = kind === "bar" ? "bar" : "time";
    // Do meio de uma lacuna, a seta vai para o bucket vizinho daquele lado.
    const current =
      cursor?.mode === "gap"
        ? nearestTimeIndex(geometry.times, cursor.time) - (event.key === "ArrowRight" ? 1 : 0)
        : cursor?.index ?? -1;
    let next: number | null = null;
    if (kind === "bar" && cursor?.mode === "gap" && (event.key === "ArrowRight" || event.key === "ArrowLeft")) {
      const right = geometry.bars.findIndex(bar => bar.from > cursor.time);
      const nextRight = right < 0 ? length : right;
      next = event.key === "ArrowRight" ? Math.min(length - 1, nextRight) : Math.max(0, nextRight - 1);
    }
    else if (event.key === "ArrowRight") next = current < 0 ? length - 1 : Math.min(length - 1, current + 1);
    else if (event.key === "ArrowLeft") next = current < 0 ? length - 1 : Math.max(0, current - 1);
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = length - 1;
    else if (event.key === "Escape") {
      if (cursor) {
        event.preventDefault();
        event.stopPropagation();
        setCursor(null);
      }
      return;
    } else return;
    event.preventDefault();
    // Em Edicao o card acima tambem escuta as setas, para MOVER o card na
    // malha. Percorrer a curva nao pode arrastar o card junto.
    event.stopPropagation();
    pointerRef.current = null;
    setCursor({ mode, index: next, sticky: true });
  };

  const focus = cursor ? resolveCursor(geometry, cursor) : null;

  // Coloca o tooltip ao lado do cursor, dentro da viewport. Fixo na tela e via
  // portal porque o card tem `overflow: hidden`: um tooltip absoluto morreria
  // cortado no primeiro card baixo.
  useLayoutEffect(() => {
    const tip = tipRef.current;
    const host = hostRef.current;
    if (!tip || !host || !focus) return;
    const rect = host.getBoundingClientRect();
    const scale = measured ? 1 : rect.width > 0 ? rect.width / FALLBACK_SIZE.w : 1;
    const anchorX = rect.left + focus.x * scale;
    const anchorY = pointerRef.current?.y ?? rect.top + rect.height / 2;
    const { offsetWidth: width, offsetHeight: height } = tip;
    const viewportW = document.documentElement.clientWidth;
    const viewportH = document.documentElement.clientHeight;
    const gap = 14;
    const left = anchorX + gap + width <= viewportW - 8 ? anchorX + gap : Math.max(8, anchorX - gap - width);
    const top = Math.min(Math.max(8, anchorY - height / 2), Math.max(8, viewportH - height - 8));
    tip.style.left = `${Math.round(left)}px`;
    tip.style.top = `${Math.round(top)}px`;
  }, [focus, measured]);

  const { padL, padT, plotH, w, h } = geometry;
  const right = w - 10;

  return (
    <div
      ref={hostRef}
      className="ts-chart"
      tabIndex={0}
      role="group"
      aria-label={`Navegar na série temporal de ${title}: setas percorrem os pontos`}
      onPointerMove={onPointerMove}
      onPointerDown={onPointerDown}
      onPointerLeave={onPointerLeave}
      onKeyDown={onKeyDown}
      onBlur={() => setCursor(null)}
    >
      <svg
        className="ts-chart__svg"
        viewBox={`0 0 ${w} ${h}`}
        role="img"
        aria-label={`Série temporal de ${title}`}
        preserveAspectRatio="none"
      >
        {/* Eixo Y: grade + rótulos de valor */}
        {geometry.yTicks.map((tick, index) => (
          <g key={`y-${index}`}>
            <line className="ts-chart__grid" x1={padL} y1={tick.y} x2={right} y2={tick.y} />
            <text className="ts-chart__ylabel" x={padL - 6} y={tick.y + 3} textAnchor="end">
              {tick.label}
            </text>
          </g>
        ))}
        {unit ? (
          <text className="ts-chart__unit" x={padL - 6} y={11} textAnchor="end">
            {unit}
          </text>
        ) : null}

        {/* Linhas de referência dos limites configurados */}
        {geometry.thresholds.map((threshold, index) => (
          <line
            key={`th-${index}`}
            className="ts-chart__threshold"
            x1={padL}
            y1={threshold.y}
            x2={right}
            y2={threshold.y}
            style={{ stroke: TONE_VAR[threshold.tone] }}
          />
        ))}

        {/* Referencia do periodo (media): tracejada, com rotulo curto. */}
        {geometry.reference ? (
          <g className="ts-chart__reference">
            <line x1={padL} y1={geometry.reference.y} x2={right} y2={geometry.reference.y} />
            <text
              x={right}
              y={geometry.reference.labelAbove ? geometry.reference.y - 4 : geometry.reference.y + 11}
              textAnchor="end"
            >
              {geometry.reference.label}
            </text>
          </g>
        ) : null}

        {/* Série */}
        {kind === "bar" ? (
          geometry.bars.map((bar) =>
            bar.blank ? (
              // Período sem valor utilizável — seja total desconhecido por
              // gap de telemetria (`unknown`), seja leitura que falhou
              // (`value === null`). Não empilha: vira marca discreta na base,
              // sem inflar a escala.
              <rect
                key={`bar-${bar.index}`}
                className="ts-chart__bar ts-chart__bar--unknown"
                x={bar.x.toFixed(1)}
                y={bar.y.toFixed(1)}
                width={bar.w.toFixed(1)}
                height={bar.h}
              >
                <title>
                  {blankReason(bar.point)}
                </title>
              </rect>
            ) : (
              <rect
                key={`bar-${bar.index}`}
                className={bar.afterGap ? "ts-chart__bar ts-chart__bar--gap" : "ts-chart__bar"}
                x={bar.x.toFixed(1)}
                y={bar.y.toFixed(1)}
                width={bar.w.toFixed(1)}
                height={bar.h.toFixed(1)}
              >
                {bar.afterGap || bar.point.includesGapDelta ? <title>{gapNote(bar.point)}</title> : null}
              </rect>
            ),
          )
        ) : (
          <>
            {/* Um <path> POR SEGMENTO contínuo. Um path único ligaria os dois
                lados de um timeout como se a leitura não tivesse parado. */}
            {geometry.series.map((serie, serieIndex) =>
              serie.segments.map((segment, index) => (
                <path
                  key={serieIndex === 0 ? `seg-${index}` : `extra-${serieIndex - 1}-${index}`}
                  className="line-chart__path"
                  d={segment}
                  style={
                    serieIndex === 0
                      ? isMultiSeries
                        ? { stroke: SERIES_COLORS[0] }
                        : undefined
                      : { stroke: SERIES_COLORS[serie.colorIndex % SERIES_COLORS.length] }
                  }
                />
              )),
            )}
            {geometry.lastDot ? (
              <circle className="line-chart__dot" cx={geometry.lastDot.x} cy={geometry.lastDot.y} r="3.5" />
            ) : null}
          </>
        )}

        {/* Cursor: linha vertical + ponto em cada serie (ou faixa da barra). */}
        {focus ? (
          <g className="ts-chart__cursor" aria-hidden="true">
            {focus.mode === "bar" ? (
              <rect x={focus.x - focus.halfWidth} y={padT} width={focus.halfWidth * 2} height={plotH} />
            ) : (
              <line
                className={focus.mode === "gap" ? "ts-chart__cursor-line--gap" : undefined}
                x1={focus.x}
                y1={padT}
                x2={focus.x}
                y2={padT + plotH}
              />
            )}
            {focus.mode === "time"
              ? focus.rows.map((row) =>
                  row.y === null ? null : (
                    <circle
                      key={row.key}
                      cx={focus.x}
                      cy={row.y}
                      r="4"
                      style={{ stroke: isMultiSeries ? SERIES_COLORS[row.colorIndex % SERIES_COLORS.length] : "var(--brand-700)" }}
                    />
                  ),
                )
              : null}
          </g>
        ) : null}

        {/* Eixo X: rótulos derivados do domínio de TEMPO (linha) ou por barra. */}
        {geometry.xTicks.map((tick, index) => (
          <text key={`x-${index}`} className="ts-chart__xlabel" x={tick.x} y={h - 7} textAnchor={tick.anchor}>
            {tick.label}
          </text>
        ))}
      </svg>

      {focus
        ? createPortal(
            <div ref={tipRef} className="ts-chart__tip" role="status" aria-live="polite">
              <div className="ts-chart__tip-when">
                {focus.mode === "gap"
                  ? describeChartInstant(focus.at, period?.timeZone)
                  : describeChartInterval(focus.at, focus.until, window, period?.timeZone)}
              </div>
              {focus.rows.map((row) => (
                <div key={row.key} className="ts-chart__tip-row">
                  {isMultiSeries ? (
                    <>
                      <span
                        className="ts-chart__tip-swatch"
                        data-series={row.colorIndex % SERIES_COLORS.length}
                        aria-hidden="true"
                      />
                      <span className="ts-chart__tip-label">{row.label}</span>
                    </>
                  ) : null}
                  <span className={row.value === null ? "ts-chart__tip-value ts-chart__tip-value--none" : "ts-chart__tip-value"}>
                    {row.value === null
                      ? row.reason
                      : `${valueLabel ? `${valueLabel} ` : ""}${formatWidgetNumber(row.value, decimals)}${unit ? ` ${unit}` : ""}`}
                  </span>
                  {row.range ? (
                    <span className="ts-chart__tip-range">
                      {`mín ${formatWidgetNumber(row.range.min, decimals)} · máx ${formatWidgetNumber(row.range.max, decimals)}`}
                    </span>
                  ) : null}
                </div>
              ))}
              {focus.note ? <div className="ts-chart__tip-note">{focus.note}</div> : null}
            </div>,
            // Dentro de dialog nativo, body fica atras do top layer e inert.
            // Conservar o tooltip no modal sem prende-lo ao overflow do card.
            hostRef.current?.closest("dialog") ?? document.body,
          )
        : null}
    </div>
  );
}, sameChartProps);

interface FocusRow {
  key: string;
  label: string;
  colorIndex: number;
  y: number | null;
  value: number | null;
  reason: string;
  range: { min: number; max: number } | null;
}

interface Focus {
  mode: Cursor["mode"];
  x: number;
  halfWidth: number;
  at: string;
  until?: string;
  rows: FocusRow[];
  note: string | null;
}

/** Traduz o cursor (indice) em coordenadas e linhas do tooltip. */
function resolveCursor(geometry: ChartGeometry, cursor: Cursor): Focus | null {
  if (cursor.mode === "bar") {
    const bar = geometry.bars[cursor.index];
    if (!bar) return null;
    const point = bar.point;
    const row: FocusRow = {
      key: "bar",
      label: "",
      colorIndex: 0,
      y: null,
      value: bar.blank ? null : point.value,
      reason: blankReason(point),
      range: null,
    };
    return {
      mode: "bar",
      x: bar.x + bar.w / 2,
      halfWidth: bar.w / 2,
      at: point.at,
      until: new Date(bar.to).toISOString(),
      rows: [row],
      note: !bar.blank && (bar.afterGap || point.includesGapDelta) ? gapNote(point) : null,
    };
  }

  if (cursor.mode === "gap") {
    const fraction = geometry.timeDomain.to > geometry.timeDomain.from
      ? (cursor.time - geometry.timeDomain.from) / (geometry.timeDomain.to - geometry.timeDomain.from)
      : 0.5;
    return {
      mode: "gap",
      x: geometry.padL + Math.min(1, Math.max(0, fraction)) * geometry.plotW,
      halfWidth: 0,
      at: new Date(cursor.time).toISOString(),
      rows: (geometry.series.length ? geometry.series : [{ label: "", colorIndex: 0 }]).map((serie, index) => ({
        key: `s-${index}`,
        label: serie.label,
        colorIndex: serie.colorIndex,
        y: null,
        value: null,
        reason: "Sem telemetria neste instante",
        range: null,
      })),
      note: null,
    };
  }

  const time = geometry.times[cursor.index];
  if (time === undefined) return null;
  let x: number | null = null;
  let at: string | null = null;
  const rows = geometry.series.map((serie, index): FocusRow => {
    const plotted = serie.byTime.get(time);
    if (plotted) {
      x ??= plotted.x;
      at ??= plotted.point.at;
    }
    const point = plotted?.point;
    const range =
      point && point.value !== null && typeof point.min === "number" && typeof point.max === "number" &&
      (point.min !== point.value || point.max !== point.value)
        ? { min: point.min, max: point.max }
        : null;
    return {
      key: `s-${index}`,
      label: serie.label,
      colorIndex: serie.colorIndex,
      y: plotted?.y ?? null,
      value: point?.value ?? null,
      reason: point
        ? UNAVAILABLE_TITLE[point.unavailable ?? "missing"]
        : "Sem telemetria neste instante",
      range,
    };
  });
  if (x === null || at === null) return null;
  return { mode: "time", x, halfWidth: 0, at, rows, note: null };
}

function sameChartProps(a: TimeSeriesChartProps, b: TimeSeriesChartProps): boolean {
  // Nao ignorar props novas: escala, titulo, limites e unidade invalidam tambem.
  return (Object.keys({ ...a, ...b }) as Array<keyof TimeSeriesChartProps>).every((key) => {
    if (key === "extraSeries") {
      const left = a.extraSeries ?? [];
      const right = b.extraSeries ?? [];
      return left.length === right.length && left.every((series, i) =>
        series.points === right[i].points && series.label === right[i].label && series.colorIndex === right[i].colorIndex);
    }
    if (key === "reference") {
      const left = a.reference ?? null;
      const right = b.reference ?? null;
      if (left === null || right === null) return left === right;
      return Object.is(left.value, right.value) && left.label === right.label;
    }
    return Object.is(a[key], b[key]);
  });
}

/**
 * Tamanho real do elemento, inteiro, ou `null` enquanto nao houver layout
 * (jsdom devolve 0 e fica no fallback). Mede antes da primeira pintura e
 * acompanha redimensionamentos do card.
 */
function useMeasuredSize(ref: RefObject<HTMLDivElement | null>): ChartSize | null {
  const [size, setSize] = useState<ChartSize | null>(null);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const apply = (width: number, height: number) => {
      const w = Math.round(width);
      const h = Math.round(height);
      if (w <= 0 || h <= 0) return;
      setSize((current) => (current && current.w === w && current.h === h ? current : { w, h }));
    };
    const rect = element.getBoundingClientRect();
    apply(rect.width, rect.height);
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) apply(entry.contentRect.width, entry.contentRect.height);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return size;
}
